import React, { useEffect, useMemo, useState, useRef } from 'react';
import DocumentCard from '../components/shared/DocumentCard';
import Pagination from '../components/shared/Pagination';
import { useOnClickOutside } from '../utils/hooks';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { DocumentService } from '../services/DocumentService';
import { downloadDocumentPdfFromDashboard } from '../services/DocumentExportService';
import { filterDocumentsVisibleForPlan } from '../utils/documentTypePlanUtils';
import {
	FILTER_STATUS_LABELS,
	FILTER_STATUS_TOOLTIPS,
	statusMatchesFilter,
} from '../utils/statusTransitions';
import { getDocumentTypeDisplayName } from '../utils/documentTypeDescriptions';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Tooltip from '../components/shared/Tooltip';

// --- TO BUILD THE DISPLAY DATA FOR DESCRIPTION ---
// function buildDescriptionFromContent(fileContent) {
// 	const title = fileContent?.title || '';
// 	const main = fileContent?.main || '';
// 	const combined = [title, main].filter(Boolean).join(': ');
// 	const snippet = combined.length > 180 ? combined.slice(0, 177) + '...' : combined;
// 	return snippet || 'No content available';
// }

// --- Filter Dropdown ---
function FilterDropdown({ value, onChange }) {
	const [open, setOpen] = useState(false);
	const panelRef = useRef(null);
	useOnClickOutside(panelRef, () => setOpen(false));

	useEffect(() => {
		const onKey = (e) => e.key === 'Escape' && setOpen(false);
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	const activeCount = (value.status.length || 0) + (value.format.length || 0) + (value.lastUpdated?.length || 0) + (value.documentType?.length || 0);

	const toggleToken = (group, token) => {
		const set = new Set(value[group]);
		set.has(token) ? set.delete(token) : set.add(token);
		onChange({ ...value, [group]: Array.from(set) });
	};

	const clearAll = () =>
		onChange({ status: [], format: [], documentType: [], lastUpdated: [] });

	const allStatuses = FILTER_STATUS_LABELS;
	const allTypes = [
		'Academic research – Student',
		'Academic research – Professional Manuscript',
		'Honors Thesis',
		"Master's Thesis",
		'PhD Dissertation'
	];
	// const allFormats = [
	// 	'APA 7th Edition',
	// ];

	const allLastUpdated = ['Past 7 days', 'Past 30 days', '30+ days'];

	const statusDescriptions = FILTER_STATUS_TOOLTIPS;

	// const formatDescriptions = {
	// 	'APA 7th Edition': 'Formatting follows APA 7th edition rules (current default).'
	// };

	const typeDescriptions = {
		'Academic research – Student': 'Student-focused academic research paper with APA-style structure.',
		'Academic research – Professional Manuscript':
			'Professional or journal-style manuscript prepared for submission.',
		'Honors Thesis': 'Undergraduate honors thesis with structured chapters and front matter.',
		"Master's Thesis": "Graduate-level master's thesis with chapters and supporting materials.",
		'PhD Dissertation': 'Doctoral dissertation with full front matter, chapters, and appendices.'
	};

	const lastUpdatedDescriptions = {
		'Past 7 days': 'Edited in the last 7 days.',
		'Past 30 days': 'Edited in the last 30 days.',
		'30+ days': 'Edited more than 30 days ago.'
	};

	return (
		<div className="relative" ref={panelRef}>
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="inline-flex h-[52px] items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-4 text-sm font-medium text-[#180A63] hover:bg-gray-50"
				aria-haspopup="menu"
				aria-expanded={open}
			>
				<span>Filters</span>
				<svg
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M0.75 6.125H23.25V8.375H0.75V6.125ZM4.5 11.375H19.5V13.625H4.5V11.375ZM9 16.625H15V18.875H9V16.625Z"
						fill="#180A63"
					/>
				</svg>
				{activeCount > 0 && (
					<span className="ml-1 rounded-full bg-[#180A63] px-2 py-0.5 text-xs font-semibold text-white">
						{activeCount}
					</span>
				)}
			</button>

			{open && (
				<div
					role="menu"
					className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
				>
					{/* Status */}
					<div className="mb-3">
						<div className="mb-2 text-xs font-semibold tracking-wide text-dark-blue ">
							Document Status
						</div>
						<div className="flex flex-wrap gap-2">
							{allStatuses.map((s) => {
								const isActive = value.status.includes(s);
								return (
									<Tooltip key={s} content={statusDescriptions[s] || ''}>
										<button
											onClick={() => toggleToken('status', s)}
											className={`rounded-full px-3 py-1 text-xs border transition ${isActive
												? "bg-dark-blue text-white border-dark-blue"
												: " border-gray bg-white text-gray-700 hover:bg-gray-50"
												}`}
										>
											{s}
										</button>
									</Tooltip>
								);
							})}
						</div>
					</div>

					<div className="my-2 h-px w-full bg-gray-100" />

					{/* Format */}
					{/* <div className="mb-3">
						<div className="mb-2 text-xs font-semibold tracking-wide text-dark-blue">
							Formatting Style
						</div>
						<div className="flex flex-wrap gap-2">
							{allFormats.map((f) => {
								const isActive = value.format.includes(f);
								return (
									<Tooltip key={f} content={formatDescriptions[f] || ''}>
										<button
											onClick={() => toggleToken('format', f)}
											className={`rounded-full px-3 py-1 text-xs border transition ${isActive
												? "bg-[#180A63] text-white border-[#180A63]"
												: "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
												}`}
										>
											{f}
										</button>
									</Tooltip>
								);
							})}
						</div>
					</div> */}

					<div className="my-2 h-px w-full bg-gray-100" />

					{/* Document Types */}
					{/* Document Types */}
					<div className="mb-3">
						<div className="mb-2 text-xs font-semibold tracking-wide text-dark-blue ">
							Document Type
						</div>
						<div className="flex flex-wrap gap-2">
							{allTypes.map((t) => {
								const isActive = value.documentType.includes(t);
								return (
									<Tooltip key={t} content={typeDescriptions[t] || ''}>
										<button
											onClick={() => toggleToken("documentType", t)}
											className={`rounded-full px-3 py-1 border text-xs transition ${isActive
												? "bg-dark-blue text-white border-dark-blue"
												: " border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
												}`}
										>
											{t}
										</button>
									</Tooltip>
								);
							})}
						</div>
					</div>

					<div className="my-2 h-px w-full bg-gray-100" />

					{/* Last Updated */}
					<div className="mb-3">
						<div className="mb-2 text-xs font-semibold tracking-wide text-dark-blue ">
							Last Updated
						</div>
						<div className="flex flex-wrap gap-2">
							{allLastUpdated.map((lu) => {
								const isActive = (value.lastUpdated || []).includes(lu);
								return (
									<Tooltip key={lu} content={lastUpdatedDescriptions[lu] || ''}>
										<button
											onClick={() => toggleToken('lastUpdated', lu)}
											className={`rounded-full px-3 py-1 text-xs border transition ${isActive
												? 'bg-dark-blue text-white border-dark-blue'
												: ' border-gray bg-white text-gray-700 hover:bg-gray-50'
												}`}
										>
											{lu}
										</button>
									</Tooltip>
								);
							})}
						</div>
					</div>


					<div className="mt-4 flex items-center justify-between">
						<Tooltip content="Clear all selected filters and show every document.">
							<button
								onClick={clearAll}
								className="text-sm font-semibold text-dark-blue underline underline-offset-2"
							>
								Reset Filters
							</button>
						</Tooltip>
						{/* <button
							onClick={() => setOpen(false)}
							className="rounded-lg bg-[#180A63] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-95"
						>
							Apply
						</button> */}
					</div>
				</div>
			)}
		</div>
	);
}

// --- Page ---
export default function DocumentsPage() {
	const [query, setQuery] = useState('');
	const [documents, setDocuments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [filters, setFilters] = useState({ status: [], format: [], documentType: [], lastUpdated: [] });
	const [currentPage, setCurrentPage] = useState(1);
	const [refreshKey, setRefreshKey] = useState(0);
	const [downloadingId, setDownloadingId] = useState(null);
	const docsPerPage = 8; // Renamed from pageSize to docsPerPage
	const navigate = useNavigate();
	const { getEffectivePlanName } = useSubscription();
	const effectivePlan = getEffectivePlanName();

	const planVisibleDocuments = useMemo(
		() => filterDocumentsVisibleForPlan(effectivePlan, documents),
		[effectivePlan, documents]
	);

	useEffect(() => {
		let mounted = true;
		async function load() {
			try {
				setLoading(true);
				setError('');
				const data = await DocumentService.fetchDocuments();
				const apiDocs = Array.isArray(data?.documents) ? data.documents : [];
				if (mounted) setDocuments(apiDocs);
			} catch (e) {
				if (mounted) setError('Failed to load documents');
			} finally {
				if (mounted) setLoading(false);
			}
		}
		load();
		return () => { mounted = false; };
	}, [refreshKey]);

	// Listen for global updates to documents and bump refreshKey to refetch
	useEffect(() => {
		const onUpdated = () => setRefreshKey((k) => k + 1);
		window.addEventListener('documents:updated', onUpdated);
		return () => window.removeEventListener('documents:updated', onUpdated);
	}, []);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		return planVisibleDocuments.filter((doc) => {
			const title = doc?.title || '';
			const description = doc?.description;
			const formatName = doc?.format?.name || '';
			const status = doc?.status || '';
			const documentTypeName = doc?.documentType?.name || '';
			const documentTypeDisplay = getDocumentTypeDisplayName(documentTypeName) || documentTypeName;
			const updatedAtIso = doc?.updatedAt;

			const matchesQuery = !q
				? true
				: [title, description, formatName, status, documentTypeName, documentTypeDisplay]
						.join(' ')
						.toLowerCase()
						.includes(q);

			const statusOk =
				filters.status.length === 0 ||
				filters.status.some((filterLabel) => statusMatchesFilter(status, filterLabel));
			const formatOk = filters.format.length === 0 || filters.format.includes(formatName);
			const docTypeOk =
				filters.documentType.length === 0 ||
				filters.documentType.includes(documentTypeDisplay);

			// Last Updated filter logic: 7 days, 30 days (includes 7), Older (>30)
			let lastUpdatedOk = true;
			if (filters.lastUpdated && filters.lastUpdated.length > 0) {
				const now = new Date();
				let diffDays = Infinity;
				if (updatedAtIso) {
					const updatedDate = new Date(updatedAtIso);
					diffDays = (now - updatedDate) / (1000 * 60 * 60 * 24);
				}
				lastUpdatedOk = filters.lastUpdated.some((opt) => {
					if (opt === 'Past 7 days') return diffDays <= 7;
					if (opt === 'Past 30 days') return diffDays <= 30;
					if (opt === '30+ days') return diffDays > 30;
					return false;
				});
			}

			return matchesQuery && statusOk && formatOk && docTypeOk && lastUpdatedOk;
		});
	}, [query, filters, planVisibleDocuments]);

	// Reset to page 1 when filters or query change
	useEffect(() => setCurrentPage(1), [query, filters]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / docsPerPage)); // Use docsPerPage

	const indexOfLastDoc = currentPage * docsPerPage;
	const indexOfFirstDoc = indexOfLastDoc - docsPerPage;
	const currentDocs = filtered.slice(indexOfFirstDoc, indexOfLastDoc); // Slice filtered documents

	// Guard if current page exceeds new total after filtering
	useEffect(() => {
		if (currentPage > totalPages) setCurrentPage(totalPages);
	}, [totalPages, currentPage]);

	const handleDownloadFromCard = async (id) => {
		if (!id || downloadingId) return;
		setDownloadingId(id);
		try {
			await downloadDocumentPdfFromDashboard(id);
		} finally {
			setDownloadingId(null);
		}
	};

	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<div className="bg-bgColor">
			{/* Header */}
			<div className="mb-12 flex flex-col items-center justify-between md:flex-row">
				<div>
					{loading ? (
						<>
							<Skeleton width={180} height={28} baseColor="#E5E7EB" highlightColor="#F3F4F6" duration={1.2} className="mb-3 rounded" />
							<Skeleton width={250} height={20} baseColor="#E5E7EB" highlightColor="#F3F4F6" duration={1.2} className="rounded" />
						</>
					) : (
						<>
							<h1 className="text-main mb-3 font-bold leading-100 text-dark-blue">
								{filtered.length === 1 ? "My Document" : "My Documents"}
							</h1>
							<p className="text-customGray text-base leading-100 md:mb-0">
							Browse, search and manage all your documents.
							</p>
						</>
					)}
				</div>

				<div className="flex items-center gap-3 flex-col md:flex-row">
					{/* Search */}
					<div className="relative w-80 my-5 md:my-0">
						<input
							type="text"
							placeholder="Search document..."
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							className="h-[52px] w-full rounded-lg border border-lightGrey pl-4 pr-10 focus:outline-none focus:border-purple"
						/>
						<svg
							className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<circle cx="11" cy="11" r="8"></circle>
							<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
						</svg>
					</div>

					{/* Filters dropdown */}
					<FilterDropdown value={filters} onChange={setFilters} />
				</div>
			</div>

			{/* Cards */}
			<div className="mb-[50px] grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
				{loading &&
					Array.from({ length: 8 }).map((_, idx) => (
						<div
							key={idx}
							className="flex flex-col items-start gap-4 sm:gap-5 w-full p-3 md:p-4 lg:p-[15px] pb-4 md:pb-5 rounded-[14px] sm:rounded-[18px] border border-[#F0F0F0] bg-white"
						>
							<div className="w-full h-32 sm:h-[150px] rounded-[14px] sm:rounded-[18px] overflow-hidden">
								<Skeleton height="100%" width="100%" baseColor="#E5E7EB" highlightColor="#F3F4F6" duration={1.2} />
							</div>
							<div className="w-full flex justify-between items-start gap-2">
								<div className="w-3/4">
									<Skeleton height={20} width="80%" />
								</div>
								<div className="w-6 h-6">
									<Skeleton circle height={24} width={24} />
								</div>
							</div>
							<div className="w-full">
								<Skeleton count={3} height={12} className="mb-1" />
							</div>
							<div className="w-full flex items-center justify-between">
								<div className="flex gap-2">
									<Skeleton height={28} width={90} />
									<Skeleton height={28} width={110} />
								</div>
								<Skeleton circle height={32} width={32} />
							</div>
						</div>
					))}

				{!loading && error && (
					<div className="col-span-full flex items-center justify-center rounded-xl border border-dashed border-red-200 p-10 text-red-600">
						{error}
					</div>
				)}

				{!loading && !error && currentDocs.map((doc) => {
					const id = doc?._id;
					const imageSrc = doc?.format?.formatpictureurl || '';
					const formatName = doc?.format?.name || '';
					const title = doc?.title || 'Untitled Document';
					const description = doc?.description;
					const status = doc?.status || '';
					return (
						<DocumentCard
							key={id}
							documentId={id}
							imageSrc={imageSrc}
							format={formatName}
							title={title}
							description={description}
							status={status}
							updatedAt={doc?.updatedAt}
							documentType={doc?.documentType?.name || ''}
							onClick={() => navigate(`/document-editor/${id}`)}
							onOpenEditor={() => navigate(`/document-editor/${id}?isCreated=true`)}
							onDownload={() => handleDownloadFromCard(id)}
							isDownloading={downloadingId === id}
						/>
					);
				})}

				{!loading && !error && currentDocs.length === 0 && (
					<div className="col-span-full flex items-center justify-center rounded-xl border border-dashed border-gray-200 p-10 text-gray-500">
						No documents yet. Start fresh or upload a draft, WriteForm will handle the structure for you.
					</div>
				)}
			</div>

			{/* Pagination Component */}
			{filtered.length > docsPerPage && (
				<Pagination
					totalDocs={filtered.length}
					docsPerPage={docsPerPage}
					onPageChange={handlePageChange}
					currentPage={currentPage}
					totalPages={totalPages}
				/>
			)}
		</div>
	);
}
